import React, { useCallback, useEffect, useRef, useState } from "react";
import { Image as ImageIcon, PenTool, Film, Aperture, FileText, Sparkles, LayoutTemplate, Figma, Paintbrush, FileType, AudioLines, Presentation, DraftingCompass, Grid3x3, Loader2, MonitorX, ScanText } from "lucide-react";
import { dapi as api } from "./deskApi";
import { toast } from "sonner";
import { formatErr } from "@/lib/api";
import PdfImageText from "./PdfImageText";

export const ARTCRAFT_APPS = {
  pdfcraft: { title: "PdfCraft", icon: FileText, w: 1100, h: 700, color: "text-red-500" },
  photocraft: { title: "PhotoCraft", icon: ImageIcon, w: 1100, h: 700, color: "text-sky-500" },
  vectorcraft: { title: "VectorCraft", icon: PenTool, w: 1100, h: 700, color: "text-orange-500" },
  filmcraft: { title: "FilmCraft", icon: Film, w: 1100, h: 700, color: "text-purple-500" },
  lightcraft: { title: "LightCraft", icon: Aperture, w: 1100, h: 700, color: "text-cyan-500" },
  effectcraft: { title: "EffectCraft", icon: Sparkles, w: 1100, h: 700, color: "text-fuchsia-500" },
  designcraft: { title: "DesignCraft", icon: LayoutTemplate, w: 1100, h: 700, color: "text-pink-500" },
  figcraft: { title: "FigCraft", icon: Figma, w: 1100, h: 700, color: "text-violet-500" },
  texturecraft: { title: "TextureCraft", icon: Paintbrush, w: 1100, h: 700, color: "text-lime-500" },
  wordcraft: { title: "WordCraft", icon: FileType, w: 1100, h: 700, color: "text-blue-500" },
  soundcraft: { title: "SoundCraft", icon: AudioLines, w: 1100, h: 700, color: "text-teal-500" },
  deckcraft: { title: "DeckCraft", icon: Presentation, w: 1100, h: 700, color: "text-amber-500" },
  cadcraft: { title: "CADCraft", icon: DraftingCompass, w: 1100, h: 700, color: "text-slate-300" },
  gridcraft: { title: "GridCraft", icon: Grid3x3, w: 1100, h: 700, color: "text-green-500" },
};

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/artcraft/run`;
const START_FAILED = /failed to start|WebGPU or WebGL2 is required|WebGL isn't supported/i;
const LOAD_FAILED = /\bfailed to load: |TypeError: (Failed to fetch|NetworkError|Load failed)/i;

// Drops this program's stored files so "Try again" downloads a fresh copy.
const clearProgramCache = async (appKey) => {
  try {
    const cache = await caches.open("artcraft-v2");
    for (const req of await cache.keys()) if (new URL(req.url).pathname.includes(`/artcraft/run/${appKey}@`)) await cache.delete(req);
  } catch { /* no cache storage */ }
};

const DownloadHelp = ({ title, onRetry }) => (
  <div className="h-full grid place-items-center bg-slate-50 p-6" data-testid="artcraft-download-help">
    <div className="max-w-md text-sm text-slate-700 space-y-3">
      <div className="flex items-center gap-2 font-semibold text-slate-900"><MonitorX className="w-5 h-5 text-amber-600" /> {title} didn&apos;t finish downloading</div>
      <p>The connection dropped while loading the program. Click Try again to download a fresh copy.</p>
      <button type="button" onClick={onRetry} className="px-3 py-1.5 rounded bg-slate-900 text-white text-xs font-semibold" data-testid="artcraft-download-retry">Try again</button>
    </div>
  </div>
);
export const MAX_ARTCRAFT_WINDOWS = 3;

// Keeps program files in the browser so they open instantly; PdfCraft is fetched first.
let warmed = false;
export const warmArtCraft = () => {
  if (warmed || !("serviceWorker" in navigator)) return;
  warmed = true;
  navigator.serviceWorker.register(`${BASE}/sw.js`).then((reg) => {
    const send = (worker) => {
      worker.postMessage({ warm: "pdfcraft" });
      const rest = Object.keys(ARTCRAFT_APPS).filter((k) => k !== "pdfcraft");
      setTimeout(() => worker.postMessage({ warmAll: rest }), 90000);
    };
    const worker = reg.active || reg.installing || reg.waiting;
    if (reg.active) send(reg.active);
    else worker?.addEventListener("statechange", () => { if (worker.state === "activated") send(worker); });
  }).catch(() => {});
};

const GraphicsHelp = ({ title, onRetry }) => (
  <div className="h-full grid place-items-center bg-slate-50 p-6" data-testid="artcraft-graphics-help">
    <div className="max-w-md text-sm text-slate-700 space-y-3">
      <div className="flex items-center gap-2 font-semibold text-slate-900"><MonitorX className="w-5 h-5 text-amber-600" /> {title} couldn&apos;t get graphics from Chrome</div>
      <p>Chrome sometimes switches graphics off for a website after many graphics windows were open. To get it back:</p>
      <ol className="list-decimal pl-5 space-y-1">
        <li>Close the other ArtCraft windows.</li>
        <li>Close <b>all</b> Chrome windows and open Chrome again.</li>
        <li>Still not working? In Chrome open <b>Settings → System</b>, turn on <b>Use graphics acceleration when available</b>, and click <b>Relaunch</b>.</li>
      </ol>
      <button type="button" onClick={onRetry} className="px-3 py-1.5 rounded bg-slate-900 text-white text-xs font-semibold" data-testid="artcraft-retry">Try again</button>
    </div>
  </div>
);

const extOf = (name) => (String(name || "").match(/\.([a-z0-9]+)$/i)?.[1] || "").toLowerCase();
const sameType = (a, b) => extOf(a) === extOf(b);

const LAST_FOLDER = "artcraft_last_save_folder";

// Folder list for "Save to File Manager"; remembers the last folder used.
const FolderPicker = ({ onPick, onBack }) => {
  const [folders, setFolders] = useState(null);
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState(() => localStorage.getItem(LAST_FOLDER) || "documents");
  useEffect(() => {
    api.get("/desktop/fs/folders").then(({ data }) => {
      const hasDocs = data.some((f) => /^File Manager › Documents$/i.test(f.path));
      setFolders(hasDocs ? data : [{ id: "documents", path: "File Manager › Documents" }, ...data]);
    }).catch(() => setFolders([{ id: "documents", path: "File Manager › Documents" }]));
  }, []);
  const list = (folders || []).filter((f) => f.path.toLowerCase().includes(query.toLowerCase()));
  const docs = (folders || []).find((f) => /^File Manager › Documents$/i.test(f.path));
  const current = picked === "documents" ? docs : (folders || []).find((f) => f.id === picked);
  const choose = () => {
    const target = current || docs;
    localStorage.setItem(LAST_FOLDER, target?.id === "documents" || !target ? "documents" : target.id);
    onPick(target && target.id !== "documents" ? target : null);
  };
  return (
    <div className="space-y-2" data-testid="artcraft-folder-picker">
      <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find a folder…" autoFocus
        className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm" data-testid="artcraft-folder-search" />
      <div className="max-h-56 overflow-y-auto rounded border border-slate-200 divide-y divide-slate-100">
        {!folders && <div className="p-3 text-xs text-slate-500">Loading folders…</div>}
        {list.map((f) => (
          <button key={f.id} type="button" onClick={() => setPicked(f.id)}
            className={`w-full text-left px-2 py-1.5 text-sm truncate ${current?.id === f.id ? "bg-sky-100 text-sky-900 font-semibold" : "hover:bg-slate-50 text-slate-700"}`}
            data-testid={`artcraft-folder-${f.id}`}>{f.path}</button>
        ))}
      </div>
      <div className="flex gap-2">
        <button type="button" onClick={onBack} className="flex-1 rounded border border-slate-300 py-1.5 text-xs" data-testid="artcraft-folder-back">Back</button>
        <button type="button" onClick={choose} disabled={!folders} className="flex-1 rounded bg-sky-600 text-white py-1.5 text-xs font-semibold disabled:opacity-50" data-testid="artcraft-folder-save">
          Save here{current ? ` (${current.path.split(" › ").pop()})` : ""}
        </button>
      </div>
    </div>
  );
};

const SaveChoice = ({ name, original, onChoose }) => {
  const [picking, setPicking] = useState(false);
  const option = (choice, label, hint, primary = false, onClick = null) => (
    <button type="button" onClick={onClick || (() => onChoose(choice))} data-testid={`artcraft-save-${choice}`}
      className={`w-full text-left rounded-md border px-3 py-2 transition-colors ${primary ? "border-sky-500 bg-sky-50 hover:bg-sky-100" : "border-slate-200 hover:bg-slate-50"}`}>
      <div className="text-sm font-semibold text-slate-900">{label}</div>
      <div className="text-xs text-slate-500">{hint}</div>
    </button>
  );
  return (
    <div className="absolute inset-0 z-10 grid place-items-center bg-slate-900/40 p-4" data-testid="artcraft-save-dialog">
      <div className="w-full max-w-sm rounded-lg bg-white shadow-2xl p-4 space-y-2">
        <div className="text-sm font-semibold text-slate-900">{picking ? "Choose a folder for" : "Save"} &ldquo;{name}&rdquo;</div>
        {picking ? <FolderPicker onBack={() => setPicking(false)} onPick={(folder) => onChoose("documents", folder)} /> : <>
          {original && option("overwrite", "Overwrite the original", `Replaces "${original}". You can undo with Restore previous version.`, true)}
          {option("documents", original ? "Save as a copy in File Manager" : "Save to File Manager", "Pick any folder — Documents is ready by default.", !original, () => setPicking(true))}
          {option("download", "Download to this computer", "Saves a copy in your browser's Downloads.")}
          <button type="button" onClick={() => onChoose("cancel")} className="w-full text-xs text-slate-500 py-1 hover:text-slate-800" data-testid="artcraft-save-cancel">Cancel</button>
        </>}
      </div>
    </div>
  );
};

// Runs an ArtCraft web build in the window; PDFs open via a short-lived same-origin link.
export default function ArtCraftApp({ appKey, file }) {
  const [fileUrl, setFileUrl] = useState(file?.id ? null : "");
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [aiOpen, setAiOpen] = useState(false);
  const [reload, setReload] = useState(0);
  const frameRef = useRef(null);
  const closeAi = useCallback(() => setAiOpen(false), []);

  useEffect(() => {
    if (!file?.id) return;
    let alive = true;
    api.post(`/desktop/fs/${file.id}/viewer-link`)
      .then(({ data }) => { if (alive) setFileUrl(`${process.env.REACT_APP_BACKEND_URL}${data.path}`); })
      .catch(() => { if (alive) setFileUrl(""); });
    return () => { alive = false; };
  }, [file?.id, reload]);

  // Same-origin frame: watch for the program's own start-up failure (message, or a canvas that never sized).
  const watch = useCallback(() => {
    let tries = 0;
    const timer = setInterval(() => {
      tries += 1;
      const win = frameRef.current?.contentWindow;
      const doc = frameRef.current?.contentDocument;
      const text = doc?.body?.innerText || "";
      const canvas = doc?.querySelector("canvas");
      // Only blame graphics once the program file has fully arrived and the screen still never sized.
      const wasmDone = (win?.performance?.getEntriesByType?.("resource") || [])
        .some((r) => /\.wasm(\?|$)/.test(r.name) && r.responseEnd > 0 && win.performance.now() - r.responseEnd > 15000);
      const blank = wasmDone && canvas && canvas.width === 300 && canvas.height === 150;
      const reason = LOAD_FAILED.test(text) ? "download" : (START_FAILED.test(text) || blank) ? "graphics" : null;
      if (reason) {
        clearInterval(timer);
        setFailed(reason);
      } else if (tries > 180) clearInterval(timer);
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => (failed || fileUrl === null ? undefined : watch()), [watch, failed, fileUrl, attempt]);

  // The injected bridge calls this whenever the program "downloads" a save; the person picks where it goes.
  const [saveQueue, setSaveQueue] = useState([]);
  const saveAsk = saveQueue[0] || null;
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    frame.__artcraftSave = (name, blob) => new Promise((resolve) => setSaveQueue((q) => [...q, { name, blob, resolve }]));
  });
  const finishSave = async (choice, folder = null) => {
    const { name, blob, resolve } = saveAsk;
    setSaveQueue((q) => q.slice(1));
    if (choice === "download") return resolve(false);
    if (choice === "cancel") return resolve(true);
    const saving = toast.loading("Saving…");
    try {
      const form = new FormData();
      const opts = { timeout: 300000 };
      if (choice === "overwrite") {
        form.append("file", blob, file.name || name);
        await api.put(`/desktop/fs/${file.id}/content`, form, opts);
        toast.success(`Saved over "${file.name}".`, { id: saving });
      } else {
        form.append("file", blob, name || file?.name || "document.pdf");
        if (folder) form.append("folder_id", folder.id);
        const { data } = await api.post("/desktop/fs/save-to-documents", form, opts);
        toast.success(`Saved "${data.name}" in ${folder ? folder.path : "File Manager › Documents"}.`, { id: saving });
      }
      window.dispatchEvent(new CustomEvent("desk-fs-changed"));
      resolve(true);
    } catch (error) {
      const why = error.response ? formatErr(error.response.data?.detail) : "The connection was too slow or dropped";
      toast.error(`${why} — downloading a copy instead.`, { id: saving });
      resolve(false);
    }
  };

  const retry = async () => { await clearProgramCache(appKey); setFailed(false); setAttempt((n) => n + 1); };
  const title = ARTCRAFT_APPS[appKey]?.title || "This program";
  if (failed === "download") return <DownloadHelp title={title} onRetry={retry} />;
  if (failed) return <GraphicsHelp title={title} onRetry={retry} />;
  if (fileUrl === null) return <div className="h-full grid place-items-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  const query = fileUrl ? `?file=${encodeURIComponent(fileUrl)}` : "";
  return (
    <div className="relative w-full h-full">
      <iframe ref={frameRef} key={attempt} src={`${BASE}/${appKey}/${query}`} title={ARTCRAFT_APPS[appKey]?.title || "ArtCraft"}
        className="w-full h-full border-0 block bg-[#f1f1f3]" allow="fullscreen; clipboard-read; clipboard-write" allowFullScreen data-testid={`artcraft-frame-${appKey}`} />
      {appKey === "pdfcraft" && file?.id && /\.pdf$/i.test(file.name || "") && !aiOpen && (
        <button type="button" onClick={() => setAiOpen(true)} title="Read and change the words in a picture or logo"
          className="absolute bottom-3 right-3 z-10 inline-flex items-center gap-1.5 rounded-full bg-slate-900/90 text-white px-3 py-1.5 text-xs font-semibold shadow-lg hover:bg-slate-800"
          data-testid="pdf-ai-open">
          <ScanText className="w-4 h-4" /> Edit image text with AI
        </button>
      )}
      {aiOpen && <PdfImageText file={file} onClose={closeAi} onSaved={() => { setAiOpen(false); setFileUrl(null); setReload((n) => n + 1); setAttempt((n) => n + 1); }} />}
      {saveAsk && <SaveChoice name={saveAsk.name} original={file?.id && sameType(saveAsk.name, file.name) ? file.name : ""} onChoose={finishSave} />}
    </div>
  );
}
